import { apiClient } from './client';
import { Photo, Annotation, ApiResponse } from '@/types';

export const photosApi = {
  // Get photo
  getPhoto: async (photoId: string) => {
    return apiClient.get<ApiResponse<Photo>>(`/photos/${photoId}`);
  },

  // Update photo caption
  updatePhotoCaption: async (photoId: string, caption: string) => {
    return apiClient.patch<ApiResponse<Photo>>(`/photos/${photoId}`, { caption });
  },

  // Delete photo
  deletePhoto: async (photoId: string) => {
    return apiClient.delete<ApiResponse<void>>(`/photos/${photoId}`);
  },

  // Add annotation
  addAnnotation: async (photoId: string, annotation: Omit<Annotation, 'id'>) => {
    return apiClient.post<ApiResponse<Annotation>>(
      `/photos/${photoId}/annotations`,
      annotation
    );
  },

  // Update annotation
  updateAnnotation: async (photoId: string, annotationId: string, data: Partial<Annotation>) => {
    return apiClient.put<ApiResponse<Annotation>>(
      `/photos/${photoId}/annotations/${annotationId}`,
      data
    );
  },

  // Delete annotation
  deleteAnnotation: async (photoId: string, annotationId: string) => {
    return apiClient.delete<ApiResponse<void>>(
      `/photos/${photoId}/annotations/${annotationId}`
    );
  },

  // Upload a photo attached to a report item. The endpoint name retains
  // "presigned-url" for backward compatibility, but the request is now a
  // multipart POST that the server forwards to R2 — R2 custom domains don't
  // accept the path-style presigned PUTs we used to issue (they 401).
  uploadPresigned: async (
    reportItemId: string,
    file: File,
    caption?: string,
    onProgress?: (progress: number) => void,
  ) => {
    const additionalData: Record<string, string> = { reportItemId };
    if (caption) additionalData.caption = caption;
    const response = await apiClient.uploadFile<ApiResponse<{ uploadUrl: string; photoUrl: string; photo: Photo }>>(
      '/photos/presigned-url',
      file,
      additionalData,
      onProgress,
    );
    if (!response.success || !response.data) throw new Error('Failed to upload photo');
    return response.data.photo;
  },

  // Upload a standalone photo (no item yet) for AI analysis.
  uploadPresignedStandalone: async (reportId: string, file: File) => {
    const response = await apiClient.uploadFile<ApiResponse<{ uploadUrl: string; photoUrl: string; photo: Photo }>>(
      '/photos/presigned-url-standalone',
      file,
      { reportId },
    );
    if (!response.success || !response.data) throw new Error('Failed to upload photo');
    return response.data.photo;
  },

  // Link an orphan photo to a report item (after AI accept)
  linkToItem: async (photoId: string, reportItemId: string) => {
    return apiClient.patch<ApiResponse<Photo>>(`/photos/${photoId}/link`, { reportItemId });
  },
};
